/* global google */
/* istanbul ignore file */
import { faExternalLink } from '@fortawesome/pro-regular-svg-icons/faExternalLink';
import { faPaperPlane } from '@fortawesome/pro-regular-svg-icons/faPaperPlane';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';
import { faPhone } from '@fortawesome/pro-solid-svg-icons/faPhone';
import { getMarkers, selectMap } from '@propertypal/shared/src/reducers/map';
import { selectProperties } from '@propertypal/shared/src/reducers/properties';
import { useAppDispatch } from '@propertypal/shared/src/reducers/store';
import CoOwnershipLogo from '@propertypal/shared/src/resources/images/co-ownership/co-ownership-logo-white.svg?url';
import HowWorks from '@propertypal/shared/src/resources/images/co-ownership/how-works.webp';
import WaysToBuy from '@propertypal/shared/src/resources/images/co-ownership/ways-to-buy.webp';
import WhatIsCoOwn from '@propertypal/shared/src/resources/images/co-ownership/what-is-co-own.webp';
import WhoCanApply from '@propertypal/shared/src/resources/images/co-ownership/who-can-apply.webp';
import WhyOnlySome from '@propertypal/shared/src/resources/images/co-ownership/why-only-some.webp';
import { trackGaLink } from '@propertypal/shared/src/services/analytics';
import axios, { API_URL } from '@propertypal/shared/src/services/axios';
import { MarkerType } from '@propertypal/shared/src/types/marker';
import { validPrefix } from '@propertypal/shared/src/utils/phoneNumber';
import { validEmail, validPhoneCharacters } from '@propertypal/shared/src/utils/regex';
import Button from '@propertypal/web-ui/src/buttons/Button';
import FontAwesomeIcon from '@propertypal/web-ui/src/icons/FontAwesomeIcon';
import PhoneNumberInput from '@propertypal/web-ui/src/inputs/PhoneNumberInput';
import TextArea from '@propertypal/web-ui/src/inputs/TextArea';
import TextInput from '@propertypal/web-ui/src/inputs/TextInput';
import LegalFeesFaqQuestion from '@propertypal/web-ui/src/legal-fees/LegalFeesFaqQuestion';
import MapProperty from '@propertypal/web-ui/src/map/MapProperty';
import PropertyGrid from '@propertypal/web-ui/src/property/PropertyGrid';
import { getCaptchaKey } from '@propertypal/web-ui/src/security/Recaptcha';
import { openToast } from '@propertypal/web-ui/src/toast/ToastContext';
import { useFormik } from 'formik';
import Link from 'next/link';
import React, { FunctionComponent, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider, useTheme } from 'styled-components';
import * as yup from 'yup';
import { MAP_IMAGE_LOCATOR } from '../../constants/imageLocator';
import GoogleMap, { getBounds, getMapInstance, getZoom } from '../map/GoogleMap';
import MapControls from '../map/MapControls';
import {
  ButtonLink,
  ButtonStyle,
  FaqContainer,
  Footer,
  FooterContent,
  FooterLeft,
  FooterRight,
  GridWrapper,
  Header,
  HeaderContent,
  HeaderImage,
  HeaderImageContent,
  HeaderText,
  Logo,
  RED,
  TextContent,
  TextLink,
  TextSection,
  TextSectionImage,
} from './CoOwnershipNewHomes.style';

const VALIDATION_SCHEMA = yup.object().shape({
  email: yup
    .string()
    .trim()
    .matches(validEmail, 'Please enter a valid email address')
    .required('Please enter your email address'),
  prefix: yup.string().trim().test(validPrefix).required('Please include a country code'),
  phone: yup
    .string()
    .trim()
    .matches(validPhoneCharacters, 'Please enter a valid phone number')
    .required('Please enter your phone number'),
  name: yup.string().trim().required('Please enter your name'),
  comments: yup.string().trim().required('Please enter a message'),
});

const CoOwnershipNewHomes: FunctionComponent = () => {
  const theme = useTheme();
  const [section, setSection] = useState('');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const infoRef = useRef<HTMLDivElement>(null);
  const whatIsRef = useRef<HTMLDivElement>(null);
  const map = useSelector(selectMap);
  const [activeMarker, setActiveMarker] = useState<MarkerType | null>(null);
  const properties = useSelector(selectProperties);
  const totalProperties = 6;
  const dispatch = useDispatch();
  const asyncDispatch = useAppDispatch();
  const formik = useFormik({
    initialValues: {
      name: '',
      phone: '',
      prefix: '+44',
      email: '',
      comments: '',
    },
    validationSchema: VALIDATION_SCHEMA,
    onSubmit: async (values) => {
      setSending(true);

      try {
        const recaptcha = await getCaptchaKey('enquire');
        await axios(
          {
            method: 'POST',
            url: `${API_URL}/enquire`,
            data: {
              ...values,
              name: values.name,
              email: values.email,
              telephone: `${values.prefix}${values.phone}`,
              comments: values.comments,
              reason: 'enquireCoOwnership',
              recaptcha,
            },
          },
          dispatch,
          undefined,
        );

        trackGaLink('co_ownership_send_enquiry', 'Contact Form', 'Co-Ownership Landing Page')();
        setSent(true);
      } catch {
        openToast({ message: 'There was an issue sending your enquiry. Please try again later.' });
      } finally {
        setSending(false);
      }
    },
  });

  const handleToggle = (key: string) => {
    if (section === key) {
      setSection('');
    } else {
      setSection(key);
    }
  };

  const handleRegionChange = async () => {
    const bounds = getBounds();
    const zoom = getZoom();

    await asyncDispatch(getMarkers(MAP_IMAGE_LOCATOR, bounds, zoom));
  };

  const onMarkerClick = (marker: MarkerType) => {
    document.getElementById('map_container')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });

    if (!marker.stacked) {
      const mapInstance = getMapInstance();

      if (mapInstance) {
        const sw = new google.maps.LatLng(marker.south, marker.west);
        const ne = new google.maps.LatLng(marker.north, marker.east);
        const bounds = new google.maps.LatLngBounds(sw, ne);

        mapInstance.fitBounds(bounds, 30);
      }
    } else {
      setActiveMarker(marker);

      const mapInstance = getMapInstance();

      if (mapInstance) {
        const center = new google.maps.LatLng(marker.lat, marker.lng);
        mapInstance.panTo(center);
      }
    }
  };

  return (
    <ThemeProvider theme={{ ...theme, primary: RED }}>
      <Header>
        <HeaderContent>
          <HeaderText>
            <Logo src={CoOwnershipLogo.src} alt="co-ownership" />

            <h1>
              New build homes <i>only</i> available through Co-Ownership
            </h1>

            <p>
              The planning system in Northern Ireland is changing, and many new housing developments now include
              &quot;affordable homes&quot;. The aim is to increase the availability of new properties for more people,
              and some of these will only be available to Co-Ownership approved customers.
            </p>

            <ButtonStyle
              href="#what-is-co-ownership"
              onClick={() => {
                trackGaLink('co_ownership_ad_click', 'Co-Ownership Ad', 'Co-Ownership Landing Page')();
              }}
            >
              Find out more about Co-Ownership
            </ButtonStyle>
          </HeaderText>

          <HeaderImage>
            <HeaderImageContent />
          </HeaderImage>
        </HeaderContent>
      </Header>

      <GridWrapper>
        <h2>
          New Homes <b>only</b> available through Co-Ownership
        </h2>

        {!properties.data?.results.length && (
          <>
            <h4>
              Sorry, there are currently no homes <b>only available through Co-Ownership</b>. You can see other homes
              that may be suitable for Co-Ownership{' '}
              <Link href="/search?coown=true&sta=forSale&st=sale&pt=residential&currency=GBP&term=3">
                here <FontAwesomeIcon icon={faChevronRight} size={22} style={{ marginBottom: -3 }} />
              </Link>
            </h4>
          </>
        )}

        {!!properties.data?.results.length && (
          <>
            <p>
              <Link href="/search?coownOnly=true&sta=forSale&st=sale&pt=residential&currency=GBP&term=3">
                View all eligible new homes <FontAwesomeIcon icon={faChevronRight} />
              </Link>
            </p>

            <PropertyGrid properties={properties.data.results.slice(0, totalProperties)} />
          </>
        )}
      </GridWrapper>

      {!!properties.data?.results.length && (
        <GoogleMap
          width="100%"
          height="calc(100vh * 0.8)"
          onMarkerClick={onMarkerClick}
          onRegionChange={handleRegionChange}
          markers={map.markers}
          activeMarker={activeMarker}
        >
          <MapControls zoomControl mapTypeControl />

          {activeMarker && activeMarker.properties?.length && (
            <MapProperty properties={activeMarker.properties} onClose={() => setActiveMarker(null)} />
          )}
        </GoogleMap>
      )}

      <TextSection>
        <TextContent id="what-is-co-ownership" ref={whatIsRef}>
          <div>
            <h2>What is Co-Ownership?</h2>

            <h4>
              Co-Ownership is the government backed and part-funded provider of shared ownership in Northern Ireland.
            </h4>

            <p>
              Their aim is to help people buy their own home, when saving for a house deposit or getting a full mortgage
              is difficult. Since 1978, they&apos;ve helped more than 35,000 local people buy their own home.
            </p>
          </div>

          <div>
            <TextSectionImage style={{ backgroundImage: `url(${WhatIsCoOwn.src})` }} />
          </div>
        </TextContent>
      </TextSection>

      <TextSection style={{ backgroundColor: 'white' }} ref={infoRef}>
        <TextContent $reverseOnMobile>
          <div>
            <TextSectionImage style={{ backgroundImage: `url(${HowWorks.src})` }} />
          </div>

          <div>
            <h2>How does Co-Ownership work?</h2>

            <p>
              Co-Ownership makes buying a home affordable, by splitting the cost into shares - your share (between 50%
              to 90% of the property) and theirs (the remaining share). This helps reduce the deposit you need - you
              might not need one at all! - and the mortgage amount you need approval for, since you&apos;re buying a
              share of a property, rather than the full thing.
            </p>

            <p>
              Each month you pay your mortgage, along with a low rent to Co-Ownership for their share, which combined is
              usually less than a month&apos;s rent in the private rental market.
            </p>

            <ButtonLink
              onClick={trackGaLink(
                'co_ownership_website_referral_click',
                'Landing Page Detail',
                'Co-Ownership Landing Page',
              )}
              href="https://bit.ly/4gsxDH6"
              target="_blank"
              rel="noreferrer"
            >
              Find out more <FontAwesomeIcon icon={faExternalLink} />
            </ButtonLink>
          </div>
        </TextContent>
      </TextSection>

      <TextSection>
        <TextContent>
          <div>
            <h2>Ways to buy through Co-Ownership</h2>

            <p>Co-Ownership offers two products:</p>

            <ul>
              <li>
                <b>Co-Own:</b> For customers who intend on using a mortgage to buy their share (starting at 50%), with
                Co-Ownership buying the rest. Some lenders don&apos;t require a deposit, making it more affordable.
              </li>
              <li>
                <b>Co-Own for Over 55s:</b> Designed for people aged 55 and over, using savings or equity from the sale
                of their current home to buy their share, and Co-Ownership buys the rest. These customers don&apos;t
                require a mortgage.
              </li>
            </ul>

            <p>
              With both options, you can increase your share of your Co-Ownership home at any time, until you own it
              outright.
            </p>
          </div>

          <div>
            <TextSectionImage style={{ backgroundImage: `url(${WaysToBuy.src})` }} />
          </div>
        </TextContent>
      </TextSection>

      <TextSection style={{ backgroundColor: 'white' }}>
        <TextContent $reverseOnMobile>
          <div>
            <TextSectionImage style={{ backgroundImage: `url(${WhoCanApply.src})` }} />
          </div>

          <div>
            <h2>Who can apply to Co-Ownership?</h2>

            <p>
              Co-Ownership is designed for people living in Northern Ireland who have a steady income. You can afford
              monthly payments but are struggling to save for a deposit or get approval for a full mortgage.
            </p>
            <p>
              To understand whether it&apos;s a good fit for you, we recommend starting with their{' '}
              <TextLink href="https://bit.ly/4gpfKbY" target="_blank" rel="noreferrer">
                eligibility checker
              </TextLink>
              . This confirms whether you&apos;re likely to be approved for Co-Ownership.
            </p>
            <p>
              Then try their{' '}
              <TextLink href="https://bit.ly/4hLsJHx" target="_blank" rel="noreferrer">
                affordability calculator
              </TextLink>{' '}
              which will give you an idea of how much it could cost each month, based on your income, deposit amount and
              finances.
            </p>
          </div>
        </TextContent>
      </TextSection>

      <TextSection>
        <TextContent>
          <div>
            <h2>Why are some properties only available through Co-Ownership?</h2>
            <p>
              Developers must include affordable homes in their new housing developments as a condition of planning
              approval. Selling these through Co-Ownership is one way in which developers meet that requirement, which
              is why you&apos;ll sometimes see homes marked as &quot;only available through Co-Ownership.&quot;
            </p>
          </div>

          <div>
            <TextSectionImage style={{ backgroundImage: `url(${WhyOnlySome.src})` }} />
          </div>
        </TextContent>
      </TextSection>

      <FaqContainer>
        <h2>Frequently Asked Questions</h2>
        <LegalFeesFaqQuestion
          id="faq1"
          title={'Is an "Only available through Co-Ownership" house different to others in the development?'}
          content={
            <p>
              No, these homes are built to the same standard as houses elsewhere in the development. The only difference
              is how they are sold.
            </p>
          }
          open={section === 'faq1'}
          toggle={handleToggle}
        />

        <LegalFeesFaqQuestion
          id="faq2"
          title="Do I have to use Co-Ownership to buy these properties?"
          content={
            <p>
              Yes, these homes are not available to buy on the open market and are only available through Co-Ownership.
            </p>
          }
          open={section === 'faq2'}
          toggle={handleToggle}
        />

        <LegalFeesFaqQuestion
          id="faq3"
          title="How do I apply for Co-Ownership?"
          content={
            <>
              <p>
                First, check your eligibility using their{' '}
                <a
                  href="https://bit.ly/4qw3GdF"
                  target="_blank"
                  rel="noreferrer"
                  onClick={trackGaLink('co_ownership_faq_click', '')}
                >
                  Eligibility Checker
                </a>
                . Then use their{' '}
                <a
                  href="https://bit.ly/4gbQzLn"
                  target="_blank"
                  rel="noreferrer"
                  onClick={trackGaLink('co_ownership_faq_calc_click', '')}
                >
                  Affordability Calculator
                </a>
                .
              </p>

              <p>When ready to apply:</p>
              <ul>
                <li>
                  Apply directly through Co-Ownership&apos;s website by creating an account, or via a financial adviser.
                </li>
                <li>
                  If approved, reserve the home and apply for a mortgage, or organise savings/equity if applying through
                  Co-Own for Over 55s.
                </li>
                <li>Co-Ownership will arrange to purchase the remaining share.</li>
              </ul>
            </>
          }
          open={section === 'faq3'}
          toggle={handleToggle}
        />

        <LegalFeesFaqQuestion
          id="faq4"
          title="What happens if in the future I want to own more of my home or all of it?"
          content={
            <p>
              You can buy more shares in your home, or buy Co-Ownership out completely, at any time. There are no
              timelines, and you can stay at your opening share or progress towards owning more (or all) at any stage.
            </p>
          }
          open={section === 'faq4'}
          toggle={handleToggle}
        />

        <LegalFeesFaqQuestion
          id="faq5"
          title="Can I only buy new-build properties through Co-Ownership?"
          content={
            <p>
              No, you can buy any type of property, including existing properties, as long as they meet their property
              criteria. Use the Co-Ownership filter on PropertyPal to see all the properties available.
            </p>
          }
          open={section === 'faq5'}
          toggle={handleToggle}
        />
      </FaqContainer>

      <Footer>
        <FooterContent>
          <FooterLeft>
            <Logo src={CoOwnershipLogo.src} alt="co-ownership" />

            <h3>
              To learn more about Co-Ownership, visit{' '}
              <a href="https://bit.ly/4v0Swi2" target="_blank" rel="noreferrer">
                co-ownership.org
              </a>{' '}
              or contact their team via the form or details below.
            </h3>

            <h4>Address</h4>
            <p>
              Co-Ownership
              <br />
              Moneda House
              <br />
              25-27 Wellington Place
              <br />
              Belfast, BT1 6GD
            </p>

            <h4 style={{ margin: '30px 0' }}>
              <FontAwesomeIcon icon={faPhone} style={{ marginRight: 8, marginBottom: -2 }} />
              +44 (0) 28 9032 7276
            </h4>

            <h4>Office opening hours</h4>
            <p>
              Monday: 9:00am - 5:00pm
              <br />
              Tuesday: 9:00am - 5:00pm
              <br />
              Wednesday: 9:30am - 5:00pm
              <br />
              Thursday: 9:00am - 5:00pm
              <br />
              Friday: 9:00am - 5:00pm
            </p>
          </FooterLeft>

          <FooterRight>
            <h2>Contact Co-Ownership</h2>

            {sent && (
              <>
                <FontAwesomeIcon icon={faPaperPlane} size={50} style={{ margin: '15px 0' }} />
                <p>
                  <b>Thank you for your enquiry!</b>
                  <br />
                  Someone from Co-Ownership will be in contact shortly.
                </p>
              </>
            )}

            {!sent && (
              <>
                <p>
                  If you have a question about Co-Ownership, complete the form below and Co-Ownership will be in touch.
                </p>

                <p>
                  To arrange a viewing or find out more about a specific property, please contact the estate agent
                  directly.
                </p>

                <form onSubmit={formik.handleSubmit}>
                  <TextInput
                    placeholder="Name"
                    name="name"
                    value={formik.values.name}
                    onValueChange={formik.handleChange('name')}
                    error={formik.touched.name && formik.errors.name}
                    containerStyle={{ margin: '15px 0' }}
                  />

                  <PhoneNumberInput
                    placeholder="Phone"
                    name="phone"
                    value={formik.values.phone}
                    onValueChange={formik.handleChange('phone')}
                    prefix={formik.values.prefix}
                    onPrefixChange={formik.handleChange('prefix')}
                    containerStyle={{ margin: '15px 0' }}
                    error={formik.touched.phone && (formik.errors.prefix || formik.errors.phone)}
                  />

                  <TextInput
                    placeholder="Email"
                    name="email"
                    type="email"
                    value={formik.values.email}
                    onValueChange={formik.handleChange('email')}
                    containerStyle={{ margin: '15px 0' }}
                    error={formik.touched.email && formik.errors.email}
                  />

                  <TextArea
                    name="body"
                    value={formik.values.comments}
                    onValueChange={formik.handleChange('comments')}
                    placeholder="Comments"
                    rows={4}
                    error={formik.touched.comments && formik.errors.comments}
                    containerStyle={{ margin: '15px 0' }}
                  />

                  <Button loading={sending} disabled={sending} color={RED}>
                    Send Enquiry
                  </Button>
                </form>
              </>
            )}
          </FooterRight>
        </FooterContent>
      </Footer>
    </ThemeProvider>
  );
};

export default CoOwnershipNewHomes;
